// SPDX-FileCopyrightText: 2024 Chris Duncan <chris@zoso.dev>
// SPDX-License-Identifier: GPL-3.0-or-later
import { bytes, hex } from './convert.js';
const { crypto } = globalThis;
const MIN = 16;
const MAX = 32;
const MOD = 4;
/**
* Represents a cryptographically strong source of entropy suitable for use in
* BIP-39 mnemonic phrase generation and consequently BIP-44 key derivation.
*
* The constructor will accept one of several different data types under certain
* constraints. If the constraints are not met, an error will be thrown. If no
* value, or the equivalent of no value, is passed to the constructor, then a
* brand new source of entropy will be generated at the maximum size of 256 bits.
*/
export class Entropy {
    #bits;
    #buffer;
    #bytes;
    #hex;
    get bits() { return this.#bits; }
    get buffer() { return this.#buffer; }
    get bytes() { return this.#bytes; }
    get hex() { return this.#hex; }
    constructor(input) {
        if (typeof input === 'number' && input > 0) {
            if (input < MIN || input > MAX) {
                throw new RangeError(`Entropy must be ${MIN}-${MAX} bytes`);
            }
            if (input % MOD !== 0) {
                throw new RangeError(`Entropy must be a multiple of ${MOD} bytes`);
            }
            this.#bytes = crypto.getRandomValues(new Uint8Array(input));
            this.#hex = bytes.toHex(this.#bytes);
            this.#bits = hex.toBin(this.#hex);
            this.#buffer = this.#bytes.buffer;
            return;
        }
        if (typeof input === 'string' && input.length > 0) {
            if (input.length < MIN * 2 || input.length > MAX * 2) {
                throw new RangeError(`Entropy must be ${MIN * 2}-${MAX * 2} characters`);
            }
            if (input.length % MOD * 2 !== 0) {
                throw new RangeError(`Entropy must be a multiple of ${MOD * 2} characters`);
            }
            this.#hex = input;
            if (!/^[0-9a-fA-F]+$/i.test(this.#hex)) {
                throw new RangeError('Entropy contains invalid hexadecimal characters');
            }
            this.#bytes = hex.toBytes(this.#hex);
            this.#bits = hex.toBin(this.#hex);
            this.#buffer = this.#bytes.buffer;
            return;
        }
        if (input instanceof ArrayBuffer && input.byteLength > 0) {
            if (input.byteLength < MIN || input.byteLength > MAX) {
                throw new Error(`Entropy must be ${MIN}-${MAX} bytes`);
            }
            if (input.byteLength % MOD !== 0) {
                throw new RangeError(`Entropy must be a multiple of ${MOD} bytes`);
            }
            this.#buffer = input;
            this.#bytes = new Uint8Array(this.#buffer);
            this.#bits = bytes.toBin(this.#bytes);
            this.#hex = bytes.toHex(this.#bytes);
            return;
        }
        if (input instanceof Uint8Array && input.length > 0) {
            if (input.length < MIN || input.length > MAX) {
                throw new Error(`Entropy must be ${MIN}-${MAX} bytes`);
            }
            if (input.length % MOD !== 0) {
                throw new RangeError(`Entropy must be a multiple of ${MOD} bytes`);
            }
            this.#bytes = input;
            this.#bits = bytes.toBin(this.#bytes);
            this.#buffer = this.#bytes.buffer;
            this.#hex = bytes.toHex(this.#bytes);
            return;
        }
        this.#bytes = crypto.getRandomValues(new Uint8Array(MAX));
        this.#hex = bytes.toHex(this.#bytes);
        this.#bits = hex.toBin(this.#hex);
        this.#buffer = this.#bytes.buffer;
    }
}
